function stripWrapperQuotes(value) {
  const token = String(value || "").trim();
  if (!token) return "";
  if (token.length >= 2 && token[0] === token[token.length - 1] && (token[0] === '"' || token[0] === "`")) {
    return token.slice(1, -1);
  }
  return token;
}

function splitIdentifierPath(value) {
  return String(value || "")
    .split(".")
    .map((part) => stripWrapperQuotes(part))
    .filter(Boolean);
}

export function normalizeIdentifierPath(value) {
  return splitIdentifierPath(value)
    .map((part) => part.toLowerCase())
    .join(".");
}

function stripQualifiedIdentifier(value) {
  return splitIdentifierPath(value).join(".");
}

function uniqueValues(values) {
  return Array.from(new Set((values || []).filter(Boolean)));
}

function escapeRegex(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function tableCandidates(tableEntry, logicalName) {
  const physicalName = stripQualifiedIdentifier(tableEntry?.physical_name || logicalName);
  const logicalRef = stripQualifiedIdentifier(tableEntry?.logical_name || tableEntry?.table_name || logicalName);
  const schemaName = stripQualifiedIdentifier(tableEntry?.schema || tableEntry?.owner);
  const dataset = stripQualifiedIdentifier(tableEntry?.dataset);
  const project = stripQualifiedIdentifier(tableEntry?.project);
  const requiredRef = stripQualifiedIdentifier(tableEntry?.required_table_ref);
  const sqlRef = stripQualifiedIdentifier(tableEntry?.sql_ref);

  return uniqueValues([
    logicalRef,
    stripQualifiedIdentifier(tableEntry?.table_name),
    physicalName,
    schemaName && physicalName ? `${schemaName}.${physicalName}` : "",
    dataset && physicalName ? `${dataset}.${physicalName}` : "",
    project && dataset && physicalName ? `${project}.${dataset}.${physicalName}` : "",
    requiredRef,
    sqlRef,
  ]);
}

export function buildSqlReferenceIndex(schema) {
  const entries = Object.entries(schema || {});
  const candidateCounts = new Map();

  entries.forEach(([logicalName, tableEntry]) => {
    tableCandidates(tableEntry, logicalName).forEach((candidate) => {
      const key = normalizeIdentifierPath(candidate);
      if (!key) return;
      candidateCounts.set(key, (candidateCounts.get(key) || 0) + 1);
    });
  });

  const tables = entries.map(([logicalName, tableEntry]) => {
    const candidates = tableCandidates(tableEntry, logicalName);
    const editorTableRef =
      candidates.find((candidate) => (candidateCounts.get(normalizeIdentifierPath(candidate)) || 0) === 1) ||
      candidates[0] ||
      logicalName;
    const canonicalTableRef =
      String(tableEntry?.sql_ref || tableEntry?.required_table_ref || tableEntry?.physical_name || logicalName || "").trim();

    const columns = Object.values(tableEntry?.columns || {}).map((column) => {
      const editorColumnName = stripQualifiedIdentifier(
        column?.name || column?.physical_name || column?.sql_ref || "",
      );
      return {
        ...column,
        editorColumnName,
        editorQualifiedRef: editorTableRef && editorColumnName ? `${editorTableRef}.${editorColumnName}` : editorColumnName,
      };
    });

    return {
      logicalName,
      tableEntry,
      editorTableRef,
      canonicalTableRef,
      qualificationRequired: Boolean(tableEntry?.qualification_required),
      variants: candidates,
      columns,
    };
  });

  return {
    tables,
    byLogicalName: Object.fromEntries(tables.map((table) => [table.logicalName, table])),
    byCanonicalRef: Object.fromEntries(
      tables.map((table) => [normalizeIdentifierPath(table.canonicalTableRef), table]).filter(([key]) => key),
    ),
  };
}

function resolveReferenceIndex(schemaOrIndex) {
  if (schemaOrIndex && Array.isArray(schemaOrIndex.tables)) {
    return schemaOrIndex;
  }
  return buildSqlReferenceIndex(schemaOrIndex || {});
}

function applyTableReplacements(sql, replacements) {
  return replacements.reduce((nextSql, replacement) => {
    const source = String(replacement.from || "").trim();
    const target = String(replacement.to || "").trim();
    if (!source || !target || source === target) {
      return nextSql;
    }

    const pattern = new RegExp(
      `(\\b(?:FROM|JOIN|UPDATE|INTO|USING|TABLE)\\s+)${escapeRegex(source)}(?=(?:\\s|,|\\)|;|$))`,
      "gi",
    );
    return nextSql.replace(pattern, `$1${target}`);
  }, String(sql || ""));
}

export function rewriteSqlForEditor(sql, schema) {
  const index = resolveReferenceIndex(schema);
  const replacements = index.tables
    .flatMap((table) =>
      uniqueValues([
        table.canonicalTableRef,
        table.tableEntry?.required_table_ref,
        table.tableEntry?.sql_ref ? stripQualifiedIdentifier(table.tableEntry.sql_ref) : "",
      ]).map((from) => ({
        from,
        to: table.editorTableRef,
      })),
    )
    .sort((a, b) => String(b.from || "").length - String(a.from || "").length);

  return applyTableReplacements(sql, replacements);
}

export function rewriteSqlForBackend(sql, schema) {
  const index = resolveReferenceIndex(schema);
  const replacements = index.tables
    .flatMap((table) =>
      uniqueValues([table.editorTableRef, ...table.variants]).map((from) => ({
        from,
        to: table.canonicalTableRef,
      })),
    )
    .sort((a, b) => String(b.from || "").length - String(a.from || "").length);

  return applyTableReplacements(sql, replacements);
}

export function getEditorTableRef(tableEntry, schemaOrIndex) {
  const index = resolveReferenceIndex(schemaOrIndex);
  const target =
    Object.values(index.byLogicalName).find((candidate) => candidate.tableEntry === tableEntry) ||
    index.byCanonicalRef[
      normalizeIdentifierPath(tableEntry?.sql_ref || tableEntry?.required_table_ref || tableEntry?.table_name)
    ];
  return target?.editorTableRef || stripQualifiedIdentifier(tableEntry?.table_name || tableEntry?.logical_name || "");
}

export function getEditorColumnRef(tableEntry, column, schemaOrIndex) {
  const tableRef = getEditorTableRef(tableEntry, schemaOrIndex);
  const columnRef = stripQualifiedIdentifier(column?.name || column?.physical_name || column?.sql_ref || "");
  return tableRef && columnRef ? `${tableRef}.${columnRef}` : columnRef;
}
